import { WhereItIs, fullLabel } from "../components/WhereItIs";
import { useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { AddToCartButton } from "@/components/AddToCartButton";
import { PartPhotoCell } from "@/components/PartPhotoCell";
import { NeedsPutawayBadge } from "@/components/NeedsPutawayBadge";
import { useSearchEngine, useResultKeyboard, FilterRail } from "@/components/search/engine";
import { StagedPartFilters } from "@/components/search/PartTapFilters";
import { SearchBox, SearchTabs, BoxExtras, useSessionTab, useOwnTagOpen, useRecentSettle } from "@/components/search/SearchBox";
import { TruckResults, PartsDeadEnd, useTabCount } from "@/components/search/TabResults";
import { MatchedLine } from "@/components/MatchBadge";
import { useRecentSearches } from "@/lib/recentSearches";
import { useScrollMemory } from "@/hooks/useScrollMemory";
import { statusLabel, statusColor, partStatusLabel, partStatusColor } from "@/lib/status";
import { titleCase } from "@/lib/text";
import { toast } from "sonner";
import { Package, Truck, MapPin, Loader2, Pencil, Download, ShoppingCart, Tag, SlidersHorizontal, CornerDownLeft, ArrowUpDown, Sparkles, X, ClipboardCheck } from "lucide-react";

export default function PartsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { has } = useAuth();
  const canBuild = has("build_cart");
  const canSell = has("flag_sell_candidate");
  const canManage = has("manage_parts");
  const canViewPricing = has("view_pricing");
  const engine = useSearchEngine({ scope: "parts", pageSize: 100, withDq: true, box: "all_parts", withCounts: true });
  const { f, setF, res, loading, run, activeKeys, dqFlags, reportOpened, setAllStates } = engine;
  const recent = useRecentSearches();
  const addRecent = recent.add;
  const [tab, setTab] = useSessionTab("all_parts", "parts");
  const trucksCount = useTabCount("trucks", f.q);
  const q = (f.q || "").trim();
  const onTrucks = !!q && tab === "trucks";
  const openPart = useCallback((p) => { reportOpened(p); addRecent(f.q); navigate(`/parts/${p.id}`, { state: fromState(location, "All Parts") }); }, [navigate, location, reportOpened, addRecent, f.q]);
  const openTruck = useCallback((d) => { addRecent(f.q); navigate(`/donors/${d.id}`, { state: fromState(location, "All Parts") }); }, [navigate, location, addRecent, f.q]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const parts = res?.parts || [];
  const total = res?.parts_total ?? 0;
  // NG-2: when the candidate window saturates the total is a LOWER BOUND — show "3,000+".
  const totalLabel = `${total.toLocaleString()}${res?.parts_total_is_lower_bound ? "+" : ""}`;
  useScrollMemory(res != null);
  const selected = parts.filter((p) => sel.has(p.id));
  const allOnPage = parts.length > 0 && parts.every((p) => sel.has(p.id));

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(() => (allOnPage ? new Set() : new Set(parts.map((p) => p.id))));
  const clearSel = () => setSel(new Set());

  const quickAddToCart = useCallback(async (part) => {
    if (part.status !== "available" || part.reserved_cart_id) { toast.error(`${part.short_code} can't be quick-added (${part.reserved_cart_id ? "it is already on a job" : "it isn't finished yet"}). Use its Add to Build/Order button — a part with no photo goes on with a reason.`); return; }
    try {
      const { data: drafts } = await api.get("/carts", { params: { status: "draft" } });
      if (!drafts.length) { toast.info("No open cart — start one from a part row's Add to Cart."); return null; }
      const { data } = await api.post(`/carts/${drafts[0].id}/add-part`, { part_id: part.id, force: false });
      toast.success(`\u2713 Added ${part.short_code} to ${data.cart_number}`); run(); return data.cart_number;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "on_other_cart") toast.warning(`${part.short_code} already on another cart.`);
      else toast.error(formatApiErrorDetail(d));
      return null;
    }
  }, [run]);

  const { selIdx } = useResultKeyboard({
    items: parts,
    onOpen: openPart,
    onCart: canBuild ? quickAddToCart : null,
  });
  const openOwnTag = useOwnTagOpen("All Parts", "search-input");
  const onEnter = (v) => {
    const t = (v || "").trim();
    if (!t) return;
    addRecent(t);
    setF({ q: t });
    openOwnTag(t);
  };
  // a recent search or a "did you mean": one tap RUNS it
  const runQ = (v) => { const t = (v || "").trim(); if (!t) return; addRecent(t); setF({ q: t }); };
  useRecentSettle(recent, res?.for_q);

  // ---- bulk actions (reuse per-item endpoints; permission-gated; donor tie & cost untouched) ----
  const bulkAddToCart = async () => {
    const targets = selected.filter((p) => p.status === "available" && !p.reserved_cart_id);
    if (!targets.length) { toast.error("None of the selected parts are available to cart."); return; }
    setBusy(true);
    try {
      const { data: drafts } = await api.get("/carts", { params: { status: "draft" } });
      if (!drafts.length) { toast.info("No open cart — start one from a part row's Add to Cart first."); return; }
      const cartId = drafts[0].id; let ok = 0, skip = 0;
      for (const p of targets) {
        try { await api.post(`/carts/${cartId}/add-part`, { part_id: p.id, force: false }); ok++; } catch { skip++; }
      }
      toast.success(`\u2713 Added ${ok} to ${drafts[0].cart_number}${skip ? ` · ${skip} skipped` : ""}`);
      clearSel(); run();
    } catch { toast.error("Bulk add failed."); } finally { setBusy(false); }
  };
  const bulkFlagSell = async () => {
    setBusy(true);
    let ok = 0;
    for (const p of selected) { try { await api.post(`/parts/${p.id}/sell-candidate`, { flag: true }); ok++; } catch { /* skip */ } }
    toast.success(`Flagged ${ok} part${ok === 1 ? "" : "s"} to sell`); clearSel(); run(); setBusy(false);
  };
  const bulkExport = () => {
    const cols = ["tag_code", "name", "brand", "make", "model", "year", "status", "grade", "location", ...(canViewPricing ? ["retail_price"] : [])];
    const head = cols.join(",");
    const rows = selected.map((p) => cols.map((c) => {
      let v = c === "location" ? fullLabel(p.location) : c === "tag_code" ? (p.tag_code || p.short_code) : (p[c] ?? "");
      v = String(v).replace(/"/g, '""');
      return /[",\n]/.test(v) ? `"${v}"` : v;
    }).join(","));
    const csv = [head, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `parts-export-${selected.length}.csv`; a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${selected.length} parts`);
  };

  return (
    <div data-testid="parts-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">All Parts</h1>
          <p className="text-sm text-slate-500">Survey the whole yard, triage data quality, and act in bulk — the warehouse &amp; management view.</p>
        </div>
        {canManage && <Button data-testid="parts-capture-btn" onClick={() => navigate("/capture")} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white h-9"><Package className="w-4 h-4 mr-1.5" />Yard capture</Button>}
      </div>

      <div className="xl:flex xl:gap-6 xl:items-start mt-4">
        <aside data-testid="parts-filter-rail" className="hidden xl:block xl:w-[340px] shrink-0 xl:sticky xl:top-20 xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
          <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />Narrow it down</div>
          <FilterRail engine={engine} pfx="" showDq collapsibleFilters withSearch={false} />
        </aside>

        <div className="flex-1 min-w-0">
          <SearchBox value={f.q} onChange={(v) => setF({ q: v })} onEnter={onEnter} onPick={runQ} recent={recent}
            placeholder="Name, OEM #, tag, keyword, truck stock #…">
            <BoxExtras engine={engine} />
          </SearchBox>
          {q && <SearchTabs tab={tab} onTab={setTab}
            counts={{ parts: (res?.for_q || "").trim() === q ? { count: total, lowerBound: res?.parts_total_is_lower_bound } : null, trucks: trucksCount }} />}
          {onTrucks ? <TruckResults q={q} onOpen={openTruck} onPick={runQ} /> : (<>
          {/* toolbar: count · mobile filters · bulk bar · keyboard hint */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                <SheetTrigger asChild>
                  <Button data-testid="mobile-filters-btn" variant="outline" className="xl:hidden rounded-lg border-slate-300 h-12 px-4 text-sm">
                    <SlidersHorizontal className="w-4 h-4 mr-1.5" />Filters{activeKeys.length + dqFlags.length ? ` · ${activeKeys.length + dqFlags.length}` : ""}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" data-testid="mobile-filters-drawer" className="w-[92vw] max-w-md overflow-y-auto p-5 pb-0">
                  <SheetHeader className="mb-3"><SheetTitle className="font-display">Filters &amp; triage</SheetTitle></SheetHeader>
                  {/* the triage list works as it always has (one tap, at once); the filters wait for "Show results" (S5) */}
                  <FilterRail engine={engine} pfx="m-" showDq withSearch={false} tapFilters={false} />
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    {filtersOpen && <StagedPartFilters engine={engine} onDone={() => setFiltersOpen(false)} />}
                  </div>
                </SheetContent>
              </Sheet>
              {res != null && (
                <div data-testid="result-count" className="font-mono text-sm">
                  <span className={`font-bold ${total ? "text-[#B01E28]" : "text-slate-400"}`}>{totalLabel}</span>
                  <span className="text-slate-500"> parts{total > parts.length ? ` · showing ${parts.length}` : ""}</span>
                  {loading && <Loader2 className="inline w-3.5 h-3.5 animate-spin ml-1.5 text-slate-400" />}
                </div>
              )}
            </div>
            {parts.length > 0 && (
              <div data-testid="keyboard-hint" className="hidden xl:flex items-center gap-2 text-[11px] text-slate-400 font-mono">
                <span className="inline-flex items-center gap-1"><ArrowUpDown className="w-3 h-3" />move</span>
                <span className="inline-flex items-center gap-1"><CornerDownLeft className="w-3 h-3" />open</span>
                {canBuild && <span className="inline-flex items-center gap-1"><kbd className="px-1 rounded border border-slate-300 bg-slate-50 text-slate-500 not-italic">C</kbd>cart</span>}
              </div>
            )}
          </div>

          {/* bulk action bar */}
          {selected.length > 0 && (
            <div data-testid="bulk-bar" className="sticky top-16 z-20 mb-3 flex items-center gap-2 flex-wrap rounded-xl border border-[#E0781A]/40 bg-orange-50 px-3 py-2 shadow-sm">
              <span className="text-sm font-semibold text-[#B45309]" data-testid="bulk-count">{selected.length} selected</span>
              <button onClick={clearSel} data-testid="bulk-clear" className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
              <div className="flex-1" />
              {canBuild && <Button data-testid="bulk-cart" disabled={busy} onClick={bulkAddToCart} size="sm" variant="outline" className="rounded-lg border-slate-300 h-8 bg-white"><ShoppingCart className="w-3.5 h-3.5 mr-1.5" />Add to cart</Button>}
              {canSell && <Button data-testid="bulk-sell" disabled={busy} onClick={bulkFlagSell} size="sm" variant="outline" className="rounded-lg border-slate-300 h-8 bg-white"><Tag className="w-3.5 h-3.5 mr-1.5" />Flag to sell</Button>}
              <Button data-testid="bulk-export" disabled={busy} onClick={bulkExport} size="sm" variant="outline" className="rounded-lg border-slate-300 h-8 bg-white"><Download className="w-3.5 h-3.5 mr-1.5" />Export CSV</Button>
              {busy && <Loader2 className="w-4 h-4 animate-spin text-[#E0781A]" />}
            </div>
          )}

          {res == null ? <div className="p-10 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>
            : total === 0 ? (
              <div data-testid="parts-empty" className="border border-dashed border-slate-300 rounded-xl bg-slate-50/50 p-6 sm:p-10">
                {f.q && (
                  <div className="mb-6 max-w-xl mx-auto">
                    <PartsDeadEnd q={f.q} res={res} onPick={runQ} testid="parts-dead-end"
                      onShowOther={() => setF({ status: "" })} onShowAll={() => setAllStates(true)} />
                  </div>
                )}
                <div className="text-center">
                  <ClipboardCheck className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="font-display font-bold text-slate-700 text-lg">Nothing matches these filters</p>
                  <p className="text-sm text-slate-500 mt-1">Clear a filter or a triage toggle to widen the survey.</p>
                </div>
              </div>
            ) : (
              <div data-testid="parts-results">
                <div className="sticky top-16 z-10 flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-200 rounded-t-xl text-[10px] font-bold uppercase tracking-wider text-slate-400 shadow-sm">
                  <Checkbox data-testid="select-all" checked={allOnPage} onCheckedChange={toggleAll} className="border-slate-300" />
                  <span>Select page</span>
                </div>
                <div className="border border-t-0 border-slate-200 bg-white rounded-b-xl overflow-hidden shadow-sm">
                  {parts.map((p, i) => (
                    <PartMgmtRow key={p.id} p={p} idx={i} selected={i === selIdx} top={!!q && i === 0 && selIdx < 0} checked={sel.has(p.id)} onCheck={() => toggleSel(p.id)}
                      navigate={navigate} onOpen={openPart} onAdded={run} canManage={canManage} canViewPricing={canViewPricing} />
                  ))}
                </div>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}

function ValueBadge({ p, canViewPricing }) {
  if (!canViewPricing) return null;
  if (p.internal_value == null) return <span data-testid={`val-none-${p.short_code}`} className="text-[10px] font-mono text-slate-300">no value</span>;
  const est = ["ai_estimate", "staff_estimate"].includes(p.value_source);
  return (
    <span className="inline-flex items-center gap-1 font-mono text-[11px] text-slate-700">
      ${p.internal_value}
      {est && <Sparkles className="w-3 h-3 text-[#B01E28]" title="Estimate · unverified" />}
    </span>
  );
}

function PartMgmtRow({ p, idx, selected, top, checked, onCheck, navigate, onOpen, onAdded, canManage, canViewPricing }) {
  const truck = titleCase([p.year, p.make, p.model].filter(Boolean).join(" "));
  return (
    <div data-testid={`parts-row-${p.short_code}`} data-back-row data-row-idx={idx} data-top-result={top ? "1" : undefined}
      className={`flex items-start gap-3 px-4 py-3 border-t border-slate-100 first:border-t-0 transition-colors ${selected ? "bg-orange-50 ring-1 ring-inset ring-[#E0781A]" : top ? "ring-2 ring-inset ring-[#E0781A] bg-orange-50/40" : checked ? "bg-orange-50/50" : "hover:bg-slate-50/70"}`}>
      <label className="-ml-2 -mt-1 w-12 h-12 shrink-0 flex items-center justify-center cursor-pointer"><Checkbox data-testid={`row-check-${p.short_code}`} checked={checked} onCheckedChange={onCheck} className="w-5 h-5 border-slate-300" /></label>
      <PartPhotoCell p={p} onChanged={onAdded} testidPrefix={`parts-photo-${p.short_code}`} />
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(p)}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-base text-slate-900 hover:text-[#B01E28] transition-colors">{titleCase(p.name) || <span className="italic text-slate-400">Unnamed</span>}</span>
          {p.is_major && <Badge className="rounded-md text-sm font-semibold bg-[#B01E28] text-white">Major</Badge>}
          {p.legacy && <Badge className="rounded-md text-sm font-semibold bg-slate-600 text-white">Legacy</Badge>}
          {p.ai_unverified && <Badge className="rounded-md text-sm font-semibold bg-red-50 text-[#B01E28] border border-dashed border-[#B01E28]"><Sparkles className="w-3.5 h-3.5 mr-0.5" />verify</Badge>}
        </div>
        <MatchedLine match={p.match} testid={`parts-matched-${p.short_code}`} />
        <div className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500 flex-wrap">
          <Truck className="w-4 h-4 text-slate-400 shrink-0" />
          {truck ? <span className="text-slate-600">{truck}</span> : <span className="italic text-slate-400">{p.donor_id ? "truck has no year / make / model yet" : "no source truck"}</span>}
          {p.category_name && <><span className="text-slate-300">·</span><span>{titleCase(p.category_name)}</span></>}
          <span className="text-slate-300">·</span><span className="font-mono text-slate-500">{p.tag_code || p.short_code}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <Badge className={`rounded-md text-white text-sm font-semibold ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
          {p.grade && <Badge variant="outline" className="rounded-md text-sm font-semibold">Grade {p.grade}</Badge>}
          <NeedsPutawayBadge part={p} />
          {p.location
            ? <WhereItIs location={p.location} size="md" testid={`parts-row-where-${p.short_code}`} />
            : <span className="font-mono text-sm text-amber-600 inline-flex items-center gap-0.5"><MapPin className="w-4 h-4" />no location</span>}
          <ValueBadge p={p} canViewPricing={canViewPricing} />
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-1.5 pt-0.5" onClick={(e) => e.stopPropagation()}>
        {canManage && <Button data-testid={`row-edit-${p.short_code}`} title="Edit in Identify workspace" size="icon" variant="outline" onClick={() => navigate(`/sort-queue/${p.id}`)} className="rounded-lg border-slate-300 h-12 w-12"><Pencil className="w-4 h-4" /></Button>}
        <AddToCartButton part={p} compact big onAdded={onAdded} />
      </div>
    </div>
  );
}
